'use client';

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useSpecialsContext } from '@/context';
import { useProducts, useSelectedStore } from '@/hooks';
import { TreezSpecials } from '@/types';
import { useSearchService } from '@/opensearch';

import { computeDealBundleState, DealBundleState } from './bundleProgress';
import { bogoGroupKey } from './utils';

type GroupQueryDescriptor = {
  dealId: string;
  groupId: string;
  groupIndex: number;
  independentGroupCount: number;
};

const buildGroupDataByDealId = (
  descriptors: GroupQueryDescriptor[],
  groupResults: (string[] | undefined)[],
): Record<string, Record<string, string[]>> => {
  const dataByDealId: Record<string, Record<string, string[]>> = {};

  descriptors.forEach((descriptor, index) => {
    const actives = groupResults[index];

    if (!actives?.length) {
      return;
    }

    if (!dataByDealId[descriptor.dealId]) {
      dataByDealId[descriptor.dealId] = {};
    }

    const groupKey = bogoGroupKey(
      descriptor.groupIndex,
      descriptor.independentGroupCount,
    );

    dataByDealId[descriptor.dealId][groupKey] = actives;
  });

  return dataByDealId;
};

/** Batch OpenSearch group lookups for multiple deals (shared `BogoProducts` cache). */
const useDealBundleStates = (
  dealIds: string[],
): {
  dealStates: DealBundleState[];
  isLoading: boolean;
} => {
  const { products } = useProducts();
  const {
    specials,
    activesPreCartDiscounts,
    isLoading: specialsLoading,
  } = useSpecialsContext();
  const { store } = useSelectedStore();
  const { getProductsIdsByGroupId } = useSearchService();

  const specialsToEvaluate = useMemo(
    () =>
      dealIds
        .map(dealId => specials?.find(sp => sp?.id === dealId))
        .filter((sp): sp is TreezSpecials => Boolean(sp)),
    [dealIds, specials],
  );

  const groupQueryDescriptors = useMemo(() => {
    const descriptors: GroupQueryDescriptor[] = [];

    specialsToEvaluate.forEach(special => {
      const independentGroupIds = special.groupsRequired ?? [];
      const dependentGroupIds = special.groups ?? [];
      const ids = [...independentGroupIds, ...dependentGroupIds];

      ids.forEach((groupId, groupIndex) => {
        descriptors.push({
          dealId: special.id,
          groupId,
          groupIndex,
          independentGroupCount: independentGroupIds.length,
        });
      });
    });

    return descriptors;
  }, [specialsToEvaluate]);

  const { groupDataByDealId, isGroupsLoading } = useQueries({
    queries: groupQueryDescriptors.map(({ groupId }) => ({
      queryKey: ['BogoProducts', groupId, store?.short_name],
      queryFn: () => getProductsIdsByGroupId({ groupId }),
      enabled: Boolean(groupId && store?.short_name),
    })),
    combine: results => ({
      groupDataByDealId: buildGroupDataByDealId(
        groupQueryDescriptors,
        results.map(result => result.data),
      ),
      isGroupsLoading: results.some(result => result.isPending),
    }),
  });

  const dealStates = useMemo(() => {
    if (specialsLoading || isGroupsLoading) {
      return [];
    }

    return specialsToEvaluate
      .map(special => {
        const state = computeDealBundleState(
          products ?? [],
          special,
          groupDataByDealId[special.id] ?? {},
          isGroupsLoading,
          activesPreCartDiscounts?.[special.id],
        );

        return state;
      })
      .filter((state): state is DealBundleState => Boolean(state));
  }, [
    activesPreCartDiscounts,
    groupDataByDealId,
    isGroupsLoading,
    products,
    specialsLoading,
    specialsToEvaluate,
  ]);

  return {
    dealStates,
    isLoading: specialsLoading || isGroupsLoading,
  };
};

export { useDealBundleStates };
